import React from 'react';

import IntlMessagesProvider from '@atlaskit/intl-messages-provider/main';
import type { I18NMessages } from '@atlaskit/intl-messages-provider/common/types';
// Import default English messages synchronously as fallback
import globalShortcutsDefaultMessages from '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/en';

import crossJoinNudgesDefaultMessages from '../../../i18n/en';

import { fetchGlobalShortcutsLanguageFile, fetchLanguageFile } from './fetch-locales';

// Merge default English messages at module level to avoid missing translation errors
const DEFAULT_MERGED_MESSAGES: I18NMessages = {
	...globalShortcutsDefaultMessages,
	...crossJoinNudgesDefaultMessages,
};

/**
 * Loader function for IntlMessagesProvider that fetches and merges messages
 * from both cross-join-nudges and global-shortcuts packages
 */
export const fetchMessagesForLocale = async (locale: string): Promise<I18NMessages | undefined> => {
	try {
		// Normalize locale - Intl.Locale doesn't like underscores
		const normalizedLocale = locale.replace('_', '-');
		const finalLocale = new Intl.Locale(normalizedLocale).toString();

		// Fetch messages from both packages in parallel
		const [crossJoinNudgesModule, globalShortcutsModule] = await Promise.all([
			fetchLanguageFile(finalLocale).catch(() => ({ default: {} })),
			fetchGlobalShortcutsLanguageFile(finalLocale).catch(() => ({ default: {} })),
		]);

		const crossJoinNudgesMessages = crossJoinNudgesModule.default || crossJoinNudgesModule;
		const globalShortcutsMessages = globalShortcutsModule.default || globalShortcutsModule;

		// Merge messages: cross-join-nudges messages take precedence over global-shortcuts
		// Start with defaults, then override with locale-specific messages
		const mergedMessages: I18NMessages = {
			...DEFAULT_MERGED_MESSAGES,
			...globalShortcutsMessages,
			...crossJoinNudgesMessages,
		};

		return mergedMessages;
	} catch (
		// eslint-disable-next-line no-unused-vars
		e
	) {
		// If loading fails, return default messages
		return DEFAULT_MERGED_MESSAGES;
	}
};

/**
 * IntlMessagesProvider component that loads messages from both
 * cross-join-nudges and global-shortcuts packages
 */
export const InvitedAppShortcutsExampleIntlProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => (
	<IntlMessagesProvider defaultMessages={DEFAULT_MERGED_MESSAGES} loaderFn={fetchMessagesForLocale}>
		{children}
	</IntlMessagesProvider>
);

/**
 * Higher Order Component that wraps a component with CrossJoinNudgesWithIntlProvider
 * This allows components to have their own Intl context without requiring
 * the parent to provide it
 */
export function withLocalIntl<P extends object>(
	Component: React.ComponentType<P>,
): (props: P) => React.JSX.Element {
	return function WithLocalIntlComponent(props: P): React.JSX.Element {
		return (
			<InvitedAppShortcutsExampleIntlProvider>
				<Component {...props} />
			</InvitedAppShortcutsExampleIntlProvider>
		);
	};
}
